import { Controller } from "@hotwired/stimulus"
import { Loader } from "@googlemaps/js-api-loader"

const GOOGLE_API_KEY = "AIzaSyAUHZaytFfL7ardR3dog_8iOSNM0DdfA14"
// Google allows at most five countries in componentRestrictions; beyond that
// we search worldwide rather than silently dropping territories.
const MAX_RESTRICTED_COUNTRIES = 5
const DEFAULT_COUNTRIES = ["gb"]
const PLACE_FIELDS = ["formatted_address", "geometry", "address_components", "name"]
const SEARCH_DEBOUNCE_MS = 200
let googleMapsPromise

export default class extends Controller {
  static targets = [
    "input",
    "latitude",
    "longitude",
    "number",
    "street",
    "locality",
    "town",
    "postcode",
    "name",
    "country",
    "results",
    "manual"
  ]

  connect() {
    this.handleInvalid = this.handleInvalid.bind(this)
    // `invalid` does not bubble, so listen in the capture phase on the form.
    this.element.addEventListener("invalid", this.handleInvalid, true)
    this.autocompleteService = null
    this.placesService = null
    this.sessionTokenClass = null
    this.sessionToken = null
    this.placesStatus = null
    this.debounceTimer = null
    this.google = null
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener("click", this.handleDocumentClick)
    this.initializePlaces()
  }

  disconnect() {
    document.removeEventListener("click", this.handleDocumentClick)
    this.element.removeEventListener("invalid", this.handleInvalid, true)
    this.clearResults()
    this.autocompleteService = null
    this.placesService = null
    this.sessionTokenClass = null
    this.sessionToken = null
    this.google = null
  }

  async initializePlaces() {
    if (!this.hasInputTarget || this.autocompleteService) {
      return
    }

    let google

    try {
      google = await this.loadGoogleMaps()
    } catch (error) {
      console.error("Failed to load Google Maps", error)
      return
    }

    if (!google?.maps) {
      return
    }

    let placesApi = google.maps.places

    if (google.maps.importLibrary) {
      try {
        placesApi = await google.maps.importLibrary("places")
      } catch (error) {
        console.warn("maps-autocomplete: unable to import places library", error)
      }
    }

    const AutocompleteServiceClass =
      placesApi?.AutocompleteService || google.maps.places?.AutocompleteService
    const PlacesServiceClass = placesApi?.PlacesService || google.maps.places?.PlacesService
    const SessionTokenClass =
      placesApi?.AutocompleteSessionToken || google.maps.places?.AutocompleteSessionToken

    if (!AutocompleteServiceClass || !PlacesServiceClass) {
      return
    }

    this.autocompleteService = new AutocompleteServiceClass()
    this.placesService = new PlacesServiceClass(document.createElement("div"))
    this.sessionTokenClass = SessionTokenClass
    this.placesStatus =
      placesApi?.PlacesServiceStatus || google.maps.places?.PlacesServiceStatus || {}
    this.google = google
  }

  async loadGoogleMaps() {
    if (!GOOGLE_API_KEY) {
      return null
    }

    if (!googleMapsPromise) {
      const loader = new Loader({
        apiKey: GOOGLE_API_KEY,
        libraries: ["places"],
        version: "weekly"
      })

      googleMapsPromise = loader.load()
    }

    return googleMapsPromise
  }

  // Country codes come from the delivery zones via a meta tag in the layout,
  // so Checkout Manager changes flow through without a JS release.
  allowedCountries() {
    const meta = document.querySelector('meta[name="shipping-countries"]')
    const codes = (meta?.content || "")
      .split(",")
      .map((code) => code.trim().toLowerCase())
      .filter(Boolean)
    return codes.length > 0 ? codes : DEFAULT_COUNTRIES
  }

  // A required field hidden inside the collapsed manual block cannot show the
  // browser's validation message, so the submit silently does nothing. Reveal
  // the block so the browser can focus the field and explain.
  handleInvalid(event) {
    if (!this.hasManualTarget || !this.manualTarget.contains(event.target)) {
      return
    }
    this.manualTarget.classList.remove("hidden")
  }

  enableInput() {
    if (this.hasInputTarget) {
      this.inputTarget.removeAttribute("readonly")
    }
  }

  search() {
    if (!this.autocompleteService || !this.hasInputTarget) {
      return
    }

    const query = this.inputTarget.value.trim()
    clearTimeout(this.debounceTimer)

    this.debounceTimer = window.setTimeout(() => {
      if (!query) {
        this.clearResults()
        return
      }

      this.requestPredictions(query)
    }, SEARCH_DEBOUNCE_MS)
  }

  requestPredictions(query) {
    if (!this.autocompleteService) {
      return
    }

    // "geocode" covers street addresses, postcodes and localities; "address"
    // only matches precise street addresses, so a bare postcode found nothing.
    const request = {
      input: query,
      types: ["geocode"],
      sessionToken: this.ensureSessionToken()
    }

    const countries = this.allowedCountries()
    if (countries.length > 0 && countries.length <= MAX_RESTRICTED_COUNTRIES) {
      request.componentRestrictions = { country: countries }
    }

    this.autocompleteService.getPlacePredictions(request, (predictions, status) => {
      const okStatus = this.placesStatus?.OK || "OK"

      if (status !== okStatus || !Array.isArray(predictions) || predictions.length === 0) {
        this.clearResults()
        return
      }

      this.renderPredictions(predictions.slice(0, 5))
    })
  }

  renderPredictions(predictions) {
    if (!this.hasResultsTarget) {
      return
    }

    this.resultsTarget.innerHTML = ""

    if (!predictions.length) {
      this.resultsTarget.classList.add("hidden")
      return
    }

    const list = document.createElement("ul")
    list.className = "divide-y divide-slate-100"
    list.setAttribute("role", "listbox")

    predictions.forEach((prediction) => {
      const description =
        prediction.description ||
        [
          prediction.structured_formatting?.main_text,
          prediction.structured_formatting?.secondary_text
        ]
          .filter(Boolean)
          .join(", ")

      const item = document.createElement("li")
      const button = document.createElement("button")
      button.type = "button"
      button.className =
        "w-full text-left px-4 py-3 text-sm hover:bg-slate-50 focus:bg-slate-100 focus:outline-none"
      button.dataset.placeId = prediction.place_id
      button.dataset.description = description
      button.setAttribute("role", "option")
      button.setAttribute("data-action", "maps-autocomplete#selectPrediction")

      const primary = document.createElement("span")
      primary.className = "block font-medium text-slate-900"
      primary.textContent = prediction.structured_formatting?.main_text || description

      const secondary = document.createElement("span")
      secondary.className = "block text-xs text-slate-500"
      secondary.textContent = prediction.structured_formatting?.secondary_text || ""

      button.appendChild(primary)
      button.appendChild(secondary)

      item.appendChild(button)
      list.appendChild(item)
    })

    this.resultsTarget.appendChild(list)
    this.resultsTarget.classList.remove("hidden")
  }

  selectPrediction(event) {
    event.preventDefault()

    const button = event.currentTarget
    const placeId = button?.dataset?.placeId
    const description = button?.dataset?.description

    if (!placeId || !this.placesService) {
      return
    }

    if (this.hasInputTarget && description) {
      this.inputTarget.value = description
    }

    this.clearResults()

    const request = {
      placeId,
      fields: PLACE_FIELDS
    }

    const token = this.sessionToken
    if (token) {
      request.sessionToken = token
    }

    this.placesService.getDetails(request, (place, status) => {
      const okStatus = this.placesStatus?.OK || "OK"
      if (status !== okStatus || !place) {
        return
      }

      this.applyPlaceDetails(place)
    })

    this.resetSessionToken()
  }

  applyPlaceDetails(place) {
    if (place.formatted_address && this.hasInputTarget) {
      this.inputTarget.value = place.formatted_address
    }

    if (place.geometry?.location) {
      this.updateCoordinates(place.geometry.location)
    }

    if (place.address_components) {
      this.updateAddressComponents(place.address_components)
      this.updateCountryTarget(place.address_components)
    }

    this.updateNameTarget(place)
    this.revealManualIfIncomplete()
  }

  // A postcode or street-only result leaves gaps the customer must fill, so
  // open the manual fields and put the cursor in the first empty one.
  revealManualIfIncomplete() {
    if (!this.hasManualTarget) {
      return
    }

    const blank = ["number", "street", "town", "postcode"]
      .map((name) => (this[`has${name[0].toUpperCase()}${name.slice(1)}Target`] ? this[`${name}Target`] : null))
      .find((field) => field && !field.value.trim())

    if (!blank) {
      return
    }

    this.manualTarget.classList.remove("hidden")
    blank.focus()
  }

  clearResults() {
    if (!this.hasResultsTarget) {
      return
    }

    this.resultsTarget.innerHTML = ""
    this.resultsTarget.classList.add("hidden")
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) {
      this.clearResults()
    }
  }

  ensureSessionToken() {
    if (this.sessionToken || !this.sessionTokenClass) {
      return this.sessionToken
    }

    this.sessionToken = new this.sessionTokenClass()
    return this.sessionToken
  }

  resetSessionToken() {
    this.sessionToken = null
  }

  updateCoordinates(location) {
    const lat = typeof location.lat === "function" ? location.lat() : location.lat
    const lng = typeof location.lng === "function" ? location.lng() : location.lng

    if (this.hasLatitudeTarget) this.latitudeTarget.value = lat || ""
    if (this.hasLongitudeTarget) this.longitudeTarget.value = lng || ""
  }

  updateAddressComponents(components) {
    const number = this.getComponent(components, "street_number")
    const street = this.getComponent(components, "route")
    const locality = this.getComponent(components, "locality")
    const town =
      this.getComponent(components, "postal_town") ||
      this.getComponent(components, "administrative_area_level_2")
    const postcode = this.getComponent(components, "postal_code")

    if (this.hasNumberTarget) this.numberTarget.value = number
    if (this.hasStreetTarget) this.streetTarget.value = street
    if (this.hasLocalityTarget) this.localityTarget.value = locality
    if (this.hasTownTarget) this.townTarget.value = town || locality
    if (this.hasPostcodeTarget) this.postcodeTarget.value = postcode
  }

  updateNameTarget(place) {
    if (!this.hasNameTarget) {
      return
    }

    const components = place.address_components || []
    const number = this.getComponent(components, "street_number")
    const placeName = place.name || ""

    if (!number && placeName) {
      this.nameTarget.value = placeName
    } else {
      this.nameTarget.value = ""
    }
  }

  updateCountryTarget(components) {
    if (!this.hasCountryTarget || !components) {
      return
    }

    const country = this.getComponent(components, "country")
    if (country) {
      this.countryTarget.value = country
    }
  }

  getComponent(components, type) {
    if (!Array.isArray(components)) {
      return ""
    }

    const component = components.find(
      (entry) => Array.isArray(entry.types) && entry.types.includes(type)
    )

    if (!component) {
      return ""
    }

    return (
      component.long_name ||
      component.longText ||
      component.short_name ||
      component.shortText ||
      ""
    )
  }
}
